/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { AngularFireDatabase } from '@angular/fire/compat/database';
import heic2any from 'heic2any';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, share, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment-variables';
import { fancyFirebaseGetters, fancyFirebaseReferencer } from '@touchplan/fancy-referencer';
import { FileViewState } from '../../modules/file-attach/attachments-view-state';
import { FileAttachCloneService } from '../../modules/file-attach/file-attach-clone.service';
import { analyticsEventsService } from '../../plain-javascript/services/analytics/AnalyticsEventsService';
import { FileType, getFileType, MimeType } from '../../utils/file-utils';
import { Attachment } from '../models/Attachment';
import { DefaultSplitDataRenderType } from '@touchplan/common';
import { Attachments } from './Attachments';
import { AbstractFileAttachApi, ImageSize, TopLevelAttachmentPaths } from './AbstractFileAttachApi';
import firebase from 'firebase/compat/app';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { ILoggerService } from '@touchplan/logging';
import { FirebaseCloudFunctionsService } from 'src/app/services/firebase-cloud-functions/cloud-functions.service';
import { HttpClient } from '@angular/common/http';
import { UPLOAD_TICKETS_ATTACHMENTS_ROUTE } from 'src/app/services/rest-api-configuration/rest-api-routes';
import { fbdbTime } from 'src/app/plain-javascript/background-scripts/fbdb-time';

export enum TicketType {
	Plan = 'plan',
	Drafts = 'drafts',
}

export interface UploadTicketAttachmentFileResponse {
	uploadId: string;
}

export class FileAttachApi extends AbstractFileAttachApi {
	/** NOTE! DO NOT USE THIS DIRECTLY! Use the one from attached-file.service */
	public file$: BehaviorSubject<Attachment[]>; // all attachments
	public renderTrigger$: Subject<string>; // used to notify file viewers when upload completed or url fetched
	private destroy$: Subject<void>;

	private readonly PROJECT_FILES = 'projectFiles';
	private readonly TICKET_ATTACHMENTS = 'ticketAttachments';

	private logger: ILoggerService = LoggerServiceFactory.create('FileAttachApi');

	constructor(
		private angularFireDatabase: AngularFireDatabase,
		private fbCloudFunctionsService: FirebaseCloudFunctionsService,
		private fileAttachCloneService: FileAttachCloneService,
		private attachments: Attachments,
		private projectId: string,
		private planId: string,
		private ticketId: string,
		private userId: string,
		private enableTicketAttachmentsGCSSegment: boolean,
		private http: HttpClient
	) {
		super();
		this.renderTrigger$ = new Subject();
		this.destroy$ = new Subject();
		this.file$ = new BehaviorSubject<Attachment[]>([]);
		attachments.data.list$
			.pipe(
				map((data: Map<string, Attachment>) => Array.from(data.values()).sort(this.sortByTime)),
				takeUntil(this.destroy$)
			)
			.subscribe(this.file$);
	}

	/**
	 * Sets/updates the "thumbnailFileUrl" and "thumbnailFetched" fields for
	 * the given attachments.
	 */
	public updateThumbnailFileUrls(data: Attachment[]): void {
		for (const file of data) {
			if (!file.view.thumbnailFetched && file.view.thumbnailFile && !file.view.thumbnailFileUrl) {
				this.getImageUrl(file, ImageSize.Thumbnail);
				file.view.thumbnailFetched = true; // don't need to trigger another emission
			}
		}
	}

	/**
	 * Sets/updates the "previewFileUrl" and "previewFetched" fields for
	 * the given attachments.
	 */
	public updatePreviewFileUrls(data: Attachment[]): void {
		for (const file of data) {
			if (!file.view.previewFetched && file.view.previewFile && !file.view.previewFileUrl) {
				this.getImageUrl(file, ImageSize.Preview);
				file.view.previewFetched = true; // don't need to trigger another emission
			}
		}
	}

	/**
	 * Gets the the file gcs blob url and caches it locally
	 * Notes: Current architecture pre-loads/joins the thumbnail and preview projectFiles except the actual file (e.g. full size image)
	 * Full file projectFile node and gcs blob url will be fetched on the fly using this function
	 * @param attachment attachment object
	 * @returns Observable of gcsBlob
	 */
	public getFileUrl$(attachment: Attachment): Observable<string> {
		const path = fancyFirebaseGetters.getProjectFilesPath(this.projectId);
		const ref = fancyFirebaseReferencer
			.getLegacyRef<firebase.database.Reference>(path, this.projectId)
			.child(this.TICKET_ATTACHMENTS)
			.child(attachment.view.fileId)
			.child('gcsBlob');
		return this.angularFireDatabase
			.object(ref)
			.valueChanges()
			.pipe(
				switchMap((blob: string) =>
					this.fbCloudFunctionsService.getFileUrl$(blob, environment.firebase.attachmentBucket)
				),
				// save the url locally
				tap((data) => this.attachments.localUpdate(attachment.$id, { fileUrl: data }))
			);
	}

	/**
	 * Array of files to be uploaded
	 * @param files files
	 */
	public addAttachment(file: File): void {
		// add a new local thumbnail
		const ticketAttachmentId = this.attachments.data.generateNewChildKey();
		if (this.isHighEfficiencyImage(file)) {
			// converted blob doesn't contain filename, get it from original file with extension
			const filename = file.name.substring(0, file.name.lastIndexOf('.')) + '.JPG';
			this.attachments.addItem(
				this.createAttachmentMetadata(this.userId, file, '', filename, MimeType.Jpeg),
				ticketAttachmentId
			);
			const blob = new Blob([file], { type: file.type });
			heic2any({
				blob,
				toType: MimeType.Jpeg,
			}).then((result) => {
				this.upload(result as Blob, ticketAttachmentId, filename);
			});
		} else {
			this.attachments.addItem(this.createAttachmentMetadata(this.userId, file), ticketAttachmentId);
			this.upload(file, ticketAttachmentId, file.name);
		}
		analyticsEventsService.fileAdded(this.planId, getFileType(file.type));
	}

	/**
	 * Delete selected attachment
	 * @param attachment attachment
	 */
	public deleteAttachment(attachment: Attachment): void {
		this.fileAttachCloneService.deleteAttachment(
			this.projectId,
			this.planId,
			this.ticketId,
			attachment.$id,
			attachment.view.fileId,
			attachment.view.thumbnailFileId,
			attachment.view.previewFileId
		);
		analyticsEventsService.fileDeleted(this.planId, attachment.view.type);
	}

	/**
	 * Download full size attachment
	 * @param attachment attachment
	 */
	public downloadAttachment(attachment: Attachment): void {
		if (!attachment.view.fileUrl) {
			this.getFileUrl$(attachment)
				.pipe(take(1), takeUntil(this.destroy$))
				.subscribe((url: string) => {
					this.download(attachment.view.filename, url);
				});
		} else {
			this.download(attachment.view.filename, attachment.view.fileUrl);
		}
	}

	// split data related operations for editing attachment data (primarily descriptions/captions);
	// invoked before switch to selected-image-view
	/**
	 * Change split data view, invoke when switch to carousel view
	 * @param attachment attachment
	 * @param renderType renderType
	 */
	public changeAttachmentView(
		attachment: Attachment,
		renderType: string = DefaultSplitDataRenderType.edit
	): void {
		this.attachments.changeView(attachment, renderType);
	}

	/**
	 * Abort edit, and preserves database view
	 * @param attachmentId attachmentId
	 * @param renderType renderType defaults to database
	 */
	public abortAttachmentView(
		attachment: Attachment,
		renderType: string = DefaultSplitDataRenderType.database
	): void {
		this.attachments.abortView(attachment, renderType);
	}

	/**
	 * Abort attachment edit, invoke when cancel is clicked on
	 * @param attachment attachment
	 * @param renderType renderType
	 */
	// public abortAttachmentView(attachmentId: string, renderType: string = DefaultSplitDataRenderType.database): void {
	// 	this.attachments.abortView(this.attachments.data.syncList.get(attachmentId), renderType);
	// }

	// invoked as user types;
	// we'll want to make all the non-description fields optional since
	// we don't currently anticipate changes to anything but the caption
	public localUpdateAttachment(attachmentId: string, payload: object): void {
		this.attachments.localUpdate(attachmentId, payload);
	}

	/**
	 * persist the attachment changes, mainly for description
	 * @param attachmentId attachmentId
	 */
	public persistAttachment(attachmentId: string, renderType: string = DefaultSplitDataRenderType.edit): void {
		const attachment = this.attachments.data.syncList.get(attachmentId);
		this.attachments.persistData(attachment, renderType);
		analyticsEventsService.fileCaptionEdit(this.planId, attachment.view.type);
	}

	/**
	 * destroy
	 */
	public destroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.renderTrigger$.complete();
		this.file$.complete();
	}

	// Retrieve image url based on given type
	private getImageUrl(attachment: Attachment, type: ImageSize): void {
		let prop: string;
		let blob: string;
		if (attachment.view.type !== FileType.Image) {
			return;
		}

		switch (type) {
			case ImageSize.Preview:
				prop = 'previewFileUrl';
				blob = attachment.view.previewFile?.gcsBlob;
				break;
			case ImageSize.Thumbnail:
				prop = 'thumbnailFileUrl';
				blob = attachment.view.thumbnailFile?.gcsBlob;
				break;
		}

		// no-op if blob is somehow undefined
		if (!blob) {
			return;
		}

		// fetch when it has not done so yet
		if (!attachment.view[prop]) {
			const ob$ = this.fbCloudFunctionsService.getFileUrl$(blob, environment.firebase.attachmentBucket);

			ob$.subscribe((url) => {
				this.attachments.localUpdate(attachment.$id, { [prop]: url });
				this.renderTrigger$.next(attachment.$id);
			});
		}
	}

	/*
	 * Start the upload process
	 * 1. Upload to Ticket Attachment API
	 * 2. Creates nodes under /projectFiles
	 * 3. Create node under /ticketAttachments
	 */
	private upload(file: File | Blob, ticketAttachmentId: string, filename: string): void {
		this.uploadToTicketAttachmentAPI$(file, ticketAttachmentId, filename)
			.pipe(take(1))
			.subscribe(({ uploadId }) => {
				const fileKey = this.attachments.data.generateNewChildKey();
				if (this.isFileImage(file as File)) {
					const thumbnailKey = this.attachments.data.generateNewChildKey();
					const previewKey = this.attachments.data.generateNewChildKey();
					this.createProjectFilesNodes(uploadId, fileKey, thumbnailKey, previewKey);
					this.updateTicketAttachmentNode(ticketAttachmentId, fileKey, thumbnailKey, previewKey);
				} else {
					this.createProjectFilesNodes(uploadId, fileKey);
					this.updateTicketAttachmentNode(ticketAttachmentId, fileKey);
				}
				file = null; // cleanup
			});
	}

	private uploadToTicketAttachmentAPI$(
		file: File | Blob,
		ticketAttachmentId: string,
		filename: string
	): Observable<UploadTicketAttachmentFileResponse> {
		const formData = new FormData();
		formData.append('file', file);
		const ob$ = this.http
			.post<UploadTicketAttachmentFileResponse>(UPLOAD_TICKETS_ATTACHMENTS_ROUTE(this.projectId), formData)
			.pipe(share());

		ob$.subscribe({
			next: () => {
				this.attachments.localUpdate(ticketAttachmentId, {
					createdAt: fbdbTime.syncTime,
				});
			},
			error: (err) => {
				this.logger.error(err);
				this.attachments.localUpdate(ticketAttachmentId, { viewState: FileViewState.Error });
				this.renderTrigger$.next(ticketAttachmentId);
			},
			complete: () => {
				const fileReader = new FileReader();
				fileReader.onload = (result) => {
					const image = result.target.result;
					this.attachments.localUpdate(ticketAttachmentId, {
						viewState: FileViewState.Default,
						// hack/optimization to use local file
						thumbnailFileUrl: image,
						previewFileUrl: image,
						fullImageUrl: image,
					});
					this.renderTrigger$.next(ticketAttachmentId);
				};
				fileReader.readAsDataURL(file);
			},
		});

		return ob$;
	}

	/**
	 * projectFilesRtdbRef should be pointed at `projectFiles/$projectId`
	 * override superclass with version that conditionally sets "nothing" or ticket attach
	 */
	protected getCloudFilesPathFromRtdbRef(
		projectFilesRtdbRef: firebase.database.Reference,
		attachmentType: '', //here to keep javascript inheritance happy, is basically ignored
		gcsFileId: string,
		suffix = ''
	) {
		let realAttachmentType: TopLevelAttachmentPaths = '';
		if (this.enableTicketAttachmentsGCSSegment) {
			realAttachmentType = this.TICKET_ATTACHMENTS;
		}
		return super.getCloudFilesPathFromRtdbRef(projectFilesRtdbRef, realAttachmentType, gcsFileId, suffix);
	}

	// don't need to wait until following writes to complete
	private createProjectFilesNodes(
		gcsFileId: string,
		fileKey: string,
		thumbnailKey?: string,
		previewKey?: string
	): void {
		const path = fancyFirebaseGetters.getProjectFilesPath(this.projectId);
		const gcsBaseRef = fancyFirebaseReferencer.getLegacyRef<firebase.database.Reference>(
			path,
			this.projectId
		);
		const ref = gcsBaseRef.child(this.TICKET_ATTACHMENTS);
		const projectFilesRefPath = this.generatePlanTicketPathRef();
		const data = {};

		data[fileKey] = {
			[this.GCS_BUCKET]: environment.firebase.attachmentBucket,
			[this.GCS_BLOB]: this.getCloudFilesPathFromRtdbRef(gcsBaseRef, '', gcsFileId),
			refs: {
				[projectFilesRefPath]: true,
			},
		};

		if (thumbnailKey) {
			data[thumbnailKey] = {
				[this.GCS_BUCKET]: environment.firebase.attachmentBucket,
				[this.GCS_BLOB]: this.getCloudFilesPathFromRtdbRef(gcsBaseRef, '', gcsFileId, this.THUMBNAIL_SUFFIX),
				refs: {
					[projectFilesRefPath]: true,
				},
			};
		}

		if (previewKey) {
			data[previewKey] = {
				[this.GCS_BUCKET]: environment.firebase.attachmentBucket,
				[this.GCS_BLOB]: this.getCloudFilesPathFromRtdbRef(gcsBaseRef, '', gcsFileId, this.PREVIEW_SUFFIX),
				refs: {
					[projectFilesRefPath]: true,
				},
			};
		}
		ref.update(data);
	}

	// node is created before upload starts, this is to update it & persist
	// don't need to wait until following writes to complete
	private updateTicketAttachmentNode(
		key: string,
		fileKey: string,
		thumbnailKey?: string,
		previewKey?: string
	): void {
		this.attachments.localUpdate(key, {
			fileId: fileKey,
			thumbnailFileId: thumbnailKey,
			previewFileId: previewKey,
		});
		this.attachments.persistData(this.attachments.data.syncList.get(key));
	}

	private download(filename: string, url: string): void {
		fetch(url)
			.then((response: Response) => response.blob())
			.then((blob: Blob) => {
				const imageUrl = URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = imageUrl;
				link.download = filename;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
			});
	}

	private generatePlanTicketPathRef(): string {
		return `tickets;${this.projectId};${this.planId};${this.ticketId}`;
	}
}
